import { EventTypes } from '@racemap/sdk/schema/events';
import classnames from 'classnames';
import classNames from 'classnames';
import { uniq } from 'lodash';
import type React from 'react';
import { type SyntheticEvent, useCallback, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import VirtualList, { type ItemStyle } from 'react-tiny-virtual-list';
import { sendAnalyticsEvent } from 'src/lib/googleAnalytics';
import { getRenderInfoBuffer } from 'src/lib/player/RenderInfoManager';
import { ALL_TAG, NULL_TAG } from 'src/lib/player/constants';
import { t } from 'src/lib/player/i18n';
import {
  getCurrentAvailableStarterGroups,
  getCurrentAvailableTags,
  getCurrentSelectedIds,
  getHiddenIds,
} from 'src/lib/player/selectors';
import {
  type EventState,
  type PoiType,
  type StarterType,
  type State,
  type TrackType,
  useStore,
} from 'src/lib/player/store';
import {
  findAllIndexes,
  flatMap,
  matchesTagSelection,
  memoizeSelector,
} from 'src/lib/player/utils';
import { useMeasure } from 'src/lib/player/utils/hooks';
import { onClickGenerator } from 'src/lib/player/utils/onClickGenerator';
import { ReloadButton } from './ReloadButton';
import SettingsView from './SettingsView';
import EventListItem from './SideBar/EventListItem';
import MultiSelect from './SideBar/MultiSelect';
import PoiListItem from './SideBar/PoiListItem';
import SortSelect from './SideBar/SortSelect';
import StarterListItem from './SideBar/StarterListItem';
import TrackListItem from './SideBar/TrackListItem';

const ITEM_HEIGHT = 59;
const HEADER_HEIGHT = 49;

enum SideBarTab {
  SEARCH = 'SEARCH',
  SETTINGS = 'SETTINGS',
}
enum SideBarSection {
  FAVORITE = 'FAVORITE',
  EVENTS = 'EVENTS',
  POI = 'POI',
  TRACK = 'TRACK',
  STARTERS = 'STARTERS',
}

enum SideBarItemType {
  HEADER = 'HEADER',
  EVENT = 'EVENT',
  STARTER = 'STARTER',
  STARTER_GROUP = 'STARTER_GROUP',
  STARTER_FILTER = 'STARTER_FILTER',
  STARTER_SORT = 'STARTER_SORT',
  POI = 'POI',
  TRACK = 'TRACK',
}
type SideBarItem =
  | {
      type: SideBarItemType.HEADER;
      label:
        | SideBarSection.FAVORITE
        | SideBarSection.POI
        | SideBarSection.TRACK
        | SideBarSection.STARTERS;
    }
  | {
      type: SideBarItemType.HEADER;
      label: SideBarSection.EVENTS;
      eventType: EventTypes;
    }
  | { type: SideBarItemType.EVENT; event: EventState }
  | { type: SideBarItemType.STARTER; starter: StarterType; isFavorite?: boolean; group?: string }
  | { type: SideBarItemType.STARTER_GROUP; tagKey: string; tagValue: string }
  | { type: SideBarItemType.STARTER_FILTER; itemCount: number }
  | { type: SideBarItemType.STARTER_SORT }
  | { type: SideBarItemType.POI; poi: PoiType; isFavorite?: boolean }
  | { type: SideBarItemType.TRACK; track: TrackType; isFavorite?: boolean };

export enum SortKey {
  START_NUMBER = 'START_NUMBER',
  NAME = 'NAME',
  OFFSET = 'OFFSET',
}

function preventDefault(event: SyntheticEvent) {
  event.preventDefault();
}

function deduplicateStarters(starters: Array<StarterType>): Array<StarterType> {
  const set: Set<string> = new Set();
  return starters.filter((starter) => {
    if (!set.has(starter.id)) {
      set.add(starter.id);
      return true;
    }
    return false;
  });
}

const getCurrentStartersAndPois = memoizeSelector(
  (state: State): [Array<StarterType>, Array<TrackType>, Array<PoiType>] => [
    flatMap(
      state.currentEventIndices.map((i) => state.events[i]),
      (event) => event.starters,
    ),
    flatMap(
      state.currentEventIndices.map((i) => state.events[i]),
      (event) => event.tracks,
    ),
    flatMap(
      state.currentEventIndices.map((i) => state.events[i]),
      (event) => event.pois,
    ).sort((a, b) => (a.offset != null ? (b.offset != null ? a.offset - b.offset : -1) : 1)),
  ],
  (state: State) => state.currentEventIndices.map((i) => state.events[i]),
);

function HideButton({ ids }: { ids: Array<string> }): JSX.Element {
  const hide = useStore((state) => state.actions.hide);
  const unhide = useStore((state) => state.actions.unhide);
  const allHidden = useStore((state) => ids.every((id) => state.timer.hidden.has(id)));

  function handleClick(
    ev:
      | React.MouseEvent<HTMLElement>
      | React.KeyboardEvent<HTMLElement>
      | React.TouchEvent<HTMLElement>,
  ) {
    ev.stopPropagation();
    if (allHidden) {
      unhide(ids);
    } else {
      hide(ids);
    }
  }

  return (
    <div
      className={classNames('starter-list-header-icon-button')}
      {...onClickGenerator(handleClick)}
    >
      {allHidden ? <i className="far fa-eye-slash" /> : <i className="far fa-eye" />}
    </div>
  );
}

export default function SideBar({ onClose }: { onClose: () => void }) {
  const setSelected = useStore((state) => state.actions.setSelected);
  const setSelectedTags = useStore((state) => state.actions.setSelectedTags);

  const [listContainerRef, listContainerBounds] = useMeasure();
  const listScrollTopRef = useRef(0);
  const listRef = useRef<VirtualList>(null);

  const [allCurrentStarters, allCurrentTracks, allCurrentPois] =
    useStore(getCurrentStartersAndPois);
  const currentEventIndices = useStore((state) => state.currentEventIndices);
  const allEvents = useStore((state) => state.events);
  const eventType = useStore((state) => state.options.type);
  const currentSelectedIds = useStore(getCurrentSelectedIds);
  const hiddenIds = useStore((state) => getHiddenIds(state.timer));
  const selectedTags = useStore((state) => state.timer.selectedTags);
  const availableTags = useStore(getCurrentAvailableTags);
  const availableStarterGroups = useStore(getCurrentAvailableStarterGroups);
  const renderInfoBuffer = useStore(getRenderInfoBuffer);

  const showEventSelector =
    (eventType === EventTypes.CONTEST_GROUP || eventType === EventTypes.STAGE_GROUP) &&
    allEvents.length > 0;

  const [searchText, setSearchText] = useState<string>('');
  const [currentTab, setCurrentTab] = useState<SideBarTab>(SideBarTab.SEARCH);
  const [isFilterOpen, setIsFilterOpen] = useState(selectedTags.size > 0);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [openSections, setOpenSections] = useState(
    showEventSelector
      ? [SideBarSection.EVENTS, SideBarSection.STARTERS, SideBarSection.FAVORITE]
      : [SideBarSection.STARTERS, SideBarSection.FAVORITE],
  );
  const [openStarterGroups, setOpenStarterGroups] = useState<Array<string>>([]);
  const [starterSorting, setStarterSorting] = useState<[SortKey, 'ASC' | 'DESC'] | null>(null);

  const filteredStarters = useMemo(() => {
    return selectedTags.size === 0
      ? allCurrentStarters
      : allCurrentStarters.filter((starter) => matchesTagSelection(starter.tags, selectedTags));
  }, [allCurrentStarters, selectedTags]);

  const deduplicatedStarters = useMemo(
    () => deduplicateStarters(filteredStarters),
    [filteredStarters],
  );

  const onToggleSection = useCallback(
    (section: SideBarSection) => {
      if (openSections.includes(section)) {
        setOpenSections(openSections.filter((s) => s !== section));
      } else {
        setOpenSections([section, ...openSections]);
      }
    },
    [openSections],
  );
  const onToggleStarterGroup = useCallback(
    (tagKey: string, tagValue: string) => {
      const key = `${tagKey}-${tagValue}`;
      if (openStarterGroups.includes(key)) {
        setOpenStarterGroups(openStarterGroups.filter((s) => s !== key));
      } else {
        setOpenStarterGroups([...openStarterGroups, key]);
      }
    },
    [openStarterGroups],
  );

  function calculateSectionOffset(sectionLabel: SideBarSection): number {
    let offset = 0;
    for (const item of items) {
      if (item.type === SideBarItemType.HEADER && item.label === sectionLabel) {
        return offset;
      }
      switch (item.type) {
        case SideBarItemType.HEADER:
          offset += HEADER_HEIGHT;
          break;
        case SideBarItemType.STARTER_GROUP:
          offset += HEADER_HEIGHT;
          break;
        default:
          offset += ITEM_HEIGHT;
          break;
      }
    }
    return 0;
  }

  function switchToSearchTab() {
    unstable_batchedUpdates(() => {
      if (currentTab !== SideBarTab.SEARCH) {
        setCurrentTab(SideBarTab.SEARCH);
      }
    });
  }

  function toggleSettingsTab() {
    setCurrentTab((currentTag) =>
      currentTag === SideBarTab.SETTINGS ? SideBarTab.SEARCH : SideBarTab.SETTINGS,
    );
  }

  function scrollCorrection(_: unknown, isSelected: boolean) {
    if (openSections.includes(SideBarSection.FAVORITE)) {
      if (listRef.current == null) return;

      const newScrollTop = isSelected
        ? listScrollTopRef.current + ITEM_HEIGHT
        : listScrollTopRef.current - ITEM_HEIGHT;
      listRef.current.rootNode.scrollTo({
        top: newScrollTop,
        behavior: 'instant',
      });
      listScrollTopRef.current = newScrollTop;
    }
  }

  function sortStarters(starters: Array<StarterType>): Array<StarterType> {
    let sortFunc: (a: StarterType, b: StarterType) => number = () => 0;
    if (starterSorting != null) {
      const sign = starterSorting[1] === 'ASC' ? 1 : -1;
      switch (starterSorting[0]) {
        case SortKey.START_NUMBER:
          sortFunc = (a: StarterType, b: StarterType) => {
            if (a.startNumber == null && b.startNumber == null) return 0;
            if (a.startNumber == null) return 1 * sign;
            if (b.startNumber == null) return -1 * sign;

            return (
              sign *
              a.startNumber.localeCompare(b.startNumber, undefined, {
                numeric: true,
                sensitivity: 'base',
              })
            );
          };
          break;
        case SortKey.NAME:
          sortFunc = (a: StarterType, b: StarterType) => {
            if (a.name == null && b.name == null) return 0;
            if (a.name == null) return 1 * sign;
            if (b.name == null) return -1 * sign;

            return sign * a.name.localeCompare(b.name);
          };
          break;
        case SortKey.OFFSET:
          sortFunc = (a: StarterType, b: StarterType) => {
            const renderIndexA = renderInfoBuffer.getRenderIndex(a.eventIndex, a.index);
            const renderIndexB = renderInfoBuffer.getRenderIndex(b.eventIndex, b.index);

            const offsetA = renderInfoBuffer.getOffset(renderIndexA);
            const offsetB = renderInfoBuffer.getOffset(renderIndexB);
            return sign * (offsetA - offsetB) || renderIndexA - renderIndexB;
          };
          break;
      }
    }
    return starters.slice().sort(sortFunc);
  }

  function renderEventHeader(eventType: EventTypes, style: ItemStyle) {
    const isOpen = openSections.includes(SideBarSection.EVENTS);
    return (
      <div
        key="header-EVENT"
        className={classnames('starter-list-header', { open: isOpen })}
        style={{
          ...style,
          height: HEADER_HEIGHT,
          zIndex: 1000 + (style.zIndex || 0),
        }}
      >
        <div
          className="starter-list-header-item"
          {...onClickGenerator(() => onToggleSection(SideBarSection.EVENTS))}
        >
          {isOpen ? (
            <i className="fas fa-chevron-down" />
          ) : (
            <i className="fas fa-chevron-down fa-rotate-270" />
          )}
          &nbsp;
          <div className="starter-list-header-item-label">{t(`list.${eventType}`)}</div>
        </div>
      </div>
    );
  }

  function renderFavoriteHeader(style: ItemStyle) {
    const anySelected = currentSelectedIds.length > 0;
    const isOpen = openSections.includes(SideBarSection.FAVORITE) && anySelected;
    return (
      <div
        key="header-FAVORITE"
        className={classnames('starter-list-header', { open: isOpen })}
        style={{
          ...style,
          height: HEADER_HEIGHT,
          zIndex: 1000 + (style.zIndex || 0),
        }}
      >
        <div
          className="starter-list-header-item"
          {...onClickGenerator(() => {
            anySelected && onToggleSection(SideBarSection.FAVORITE);
          })}
        >
          <div
            style={{
              display: 'flex',
              flex: 1,
              justifyContent: 'center',
              alignItems: 'center',
              opacity: anySelected ? 1 : 0.5,
            }}
          >
            {isOpen ? (
              <i className="fas fa-chevron-down" />
            ) : (
              <i className="fas fa-chevron-down fa-rotate-270" />
            )}
            &nbsp;
            <div className="starter-list-header-item-label">{t('list.FAVORITE')}</div>
            {anySelected && (
              <div
                className={classNames('starter-list-header-icon-button')}
                {...onClickGenerator((event) => {
                  event.stopPropagation();
                  setSelected([]);
                })}
              >
                <i className="fas fa-times" />
              </div>
            )}
            {anySelected && <HideButton ids={currentSelectedIds} />}
          </div>
        </div>
      </div>
    );
  }

  function renderTrackHeader(style: ItemStyle) {
    const isOpen = openSections.includes(SideBarSection.TRACK);
    return (
      <div
        key="header-TRACK"
        className={classnames('starter-list-header', { open: isOpen })}
        style={{
          ...style,
          height: HEADER_HEIGHT,
          zIndex: 1000 + (style.zIndex || 0),
        }}
      >
        <div
          className="starter-list-header-item"
          {...onClickGenerator(() => {
            onToggleSection(SideBarSection.TRACK);
          })}
        >
          {isOpen ? (
            <i className="fas fa-chevron-down" />
          ) : (
            <i className="fas fa-chevron-down fa-rotate-270" />
          )}
          &nbsp;
          <div className="starter-list-header-item-label">{t('list.TRACK')}</div>
          <HideButton ids={allCurrentTracks.map((track) => track.id)} />
        </div>
      </div>
    );
  }

  function renderPoiHeader(style: ItemStyle) {
    const isOpen = openSections.includes(SideBarSection.POI);
    return (
      <div
        key="header-POI"
        className={classnames('starter-list-header', { open: isOpen })}
        style={{
          ...style,
          height: HEADER_HEIGHT,
          zIndex: 1000 + (style.zIndex || 0),
        }}
      >
        <div
          className="starter-list-header-item"
          {...onClickGenerator(() => {
            onToggleSection(SideBarSection.POI);
          })}
        >
          {isOpen ? (
            <i className="fas fa-chevron-down" />
          ) : (
            <i className="fas fa-chevron-down fa-rotate-270" />
          )}
          &nbsp;
          <div className="starter-list-header-item-label">{t('list.POI')}</div>
          <HideButton ids={allCurrentPois.map((poi) => poi.id)} />
        </div>
      </div>
    );
  }

  function renderStarterHeader(style: ItemStyle) {
    const isOpen = openSections.includes(SideBarSection.STARTERS);

    return (
      <div
        key="header-STARTERS"
        className={classnames('starter-list-header', { open: isOpen })}
        style={{
          ...style,
          height: HEADER_HEIGHT,
          zIndex: 1000 + (style.zIndex || 0),
        }}
      >
        <div
          className="starter-list-header-item"
          {...onClickGenerator(() => {
            onToggleSection(SideBarSection.STARTERS);
          })}
        >
          {isOpen ? (
            <i className="fas fa-chevron-down" />
          ) : (
            <i className="fas fa-chevron-down fa-rotate-270" />
          )}
          &nbsp;
          <div className="starter-list-header-item-label">
            {t('list.STARTERS')}
            {selectedTags.size > 0 && (
              <span className="starter-list-header-item-info">
                {filteredStarters.length} / {allCurrentStarters.length} {t('list.N_FILTERED')}
              </span>
            )}
          </div>
          <div
            className={classNames('starter-list-header-icon-button', {
              active: isSortOpen && isOpen,
            })}
            {...onClickGenerator((event) => {
              event.stopPropagation();
              const sortBoxOffset = calculateSectionOffset(SideBarSection.STARTERS) + HEADER_HEIGHT;
              const sortBoxIsInView =
                listScrollTopRef.current + HEADER_HEIGHT <= sortBoxOffset &&
                sortBoxOffset <=
                  listScrollTopRef.current + (listRef.current?.rootNode.clientHeight || 0);
              if (isSortOpen && sortBoxIsInView) {
                setIsSortOpen(false);
              } else {
                setIsSortOpen(true);
                setIsFilterOpen(false);
                if (!sortBoxIsInView) {
                  listRef.current?.rootNode.scrollTo({
                    top: sortBoxOffset - HEADER_HEIGHT,
                    behavior: 'smooth',
                  });
                  listScrollTopRef.current = sortBoxOffset - HEADER_HEIGHT;
                }
              }
            })}
          >
            <i className="fas fa-sort-amount-down" />
          </div>
          {availableTags.size > 0 && (
            <div
              className={classNames('starter-list-header-icon-button', {
                active: isFilterOpen && isOpen,
              })}
              {...onClickGenerator((event) => {
                event.stopPropagation();
                const filterBoxOffset =
                  calculateSectionOffset(SideBarSection.STARTERS) + HEADER_HEIGHT;
                const filterBoxIsInView =
                  listScrollTopRef.current + HEADER_HEIGHT <= filterBoxOffset &&
                  filterBoxOffset <=
                    listScrollTopRef.current + (listRef.current?.rootNode.clientHeight || 0);
                if (isFilterOpen && filterBoxIsInView) {
                  setIsFilterOpen(false);
                } else {
                  setIsFilterOpen(true);
                  setIsSortOpen(false);
                  if (!filterBoxIsInView) {
                    listRef.current?.rootNode.scrollTo({
                      top: filterBoxOffset - HEADER_HEIGHT,
                      behavior: 'smooth',
                    });
                    listScrollTopRef.current = filterBoxOffset - HEADER_HEIGHT;
                  }
                }
              })}
            >
              <i className="fas fa-filter" />
            </div>
          )}
          <HideButton ids={allCurrentStarters.map((starter) => starter.id)} />
        </div>
      </div>
    );
  }

  function renderStarterGroupHeader(style: ItemStyle, tagKey: string, tagValue: string) {
    const isOpen = openStarterGroups.includes(`${tagKey}-${tagValue}`);

    const startersInGroup =
      tagKey === ALL_TAG
        ? deduplicatedStarters
        : deduplicatedStarters.filter(
            (starter) =>
              starter.tags.get(tagKey) === tagValue ||
              (tagValue === NULL_TAG && !starter.tags.has(tagKey)),
          );

    return (
      <div
        key={`header-STARTER-GROUP-${tagKey}-${tagValue}`}
        className={classnames('starter-list-header starter-list-group-header', { open: isOpen })}
        style={{
          ...style,
          height: HEADER_HEIGHT,
          zIndex: 1000 + (style.zIndex || 0),
        }}
      >
        <div
          className="starter-list-header-item"
          {...onClickGenerator(() => {
            onToggleStarterGroup(tagKey, tagValue);
          })}
        >
          {isOpen ? (
            <i className="fas fa-chevron-down" />
          ) : (
            <i className="fas fa-chevron-down fa-rotate-270" />
          )}
          &nbsp;
          <div className="starter-list-header-item-label">
            {tagKey === ALL_TAG ? (
              t('tags.all')
            ) : (
              <>
                {t(`tags.${tagKey}`, tagKey)}:&nbsp;
                {tagValue === NULL_TAG ? t('tags.none') : tagValue}
              </>
            )}
            &nbsp;<span className="starter-list-header-item-info">{startersInGroup.length}</span>
          </div>
          <HideButton ids={startersInGroup.map((starter) => starter.id)} />
        </div>
      </div>
    );
  }

  function renderEventRow(event: EventState, style: ItemStyle) {
    return (
      <div key={event.eventId} style={style}>
        <EventListItem event={event} />
      </div>
    );
  }

  function renderStarterRow(
    starter: StarterType,
    style: ItemStyle,
    isFavorite = false,
    isGroup: string | null = null,
  ) {
    const renderIndex = renderInfoBuffer.getRenderIndex(starter.eventIndex, starter.index);
    return (
      <div key={`${starter.id}-${isFavorite}-${isGroup}`} style={style}>
        <StarterListItem
          starter={starter}
          starterOpacity={
            currentEventIndices.includes(starter.eventIndex) && !hiddenIds.has(starter.id)
              ? renderInfoBuffer.getOpacity(renderIndex)
              : 0
          }
          starterOffset={renderInfoBuffer.getOffset(renderIndex)}
          starterTimestamp={renderInfoBuffer.getTimestamp(renderIndex)}
          onClick={scrollCorrection}
        />
      </div>
    );
  }

  function renderStarterFilterRow(style: ItemStyle) {
    return (
      <div key="starter-filter-row" className="starter-list-filter" style={style}>
        {Array.from(availableTags.keys(), (tagKey) => (
          <div key={`tag-select-${tagKey}`}>
            <MultiSelect tagKey={tagKey} />
          </div>
        ))}
        <div key="deselect-tag-button">
          <div
            className={classnames('starter-list-button', {
              'starter-list-button-disabled': selectedTags.size === 0,
            })}
            {...onClickGenerator(() => {
              setSelectedTags(new Map());
              setIsFilterOpen(false);
            })}
          >
            {t('list.DESELECT_TAG')}
          </div>
        </div>
      </div>
    );
  }

  function renderStarterSortRow(style: ItemStyle) {
    return (
      <div key="starter-sort-row" className="starter-list-filter" style={style}>
        <div key="sort-start-number">
          <SortSelect
            sortKey={SortKey.START_NUMBER}
            sortDirection={starterSorting?.[0] === SortKey.START_NUMBER ? starterSorting[1] : null}
            onSort={(direction) => setStarterSorting([SortKey.START_NUMBER, direction])}
          />
        </div>
        <div key="sort-name">
          <SortSelect
            sortKey={SortKey.NAME}
            sortDirection={starterSorting?.[0] === SortKey.NAME ? starterSorting[1] : null}
            onSort={(direction) => setStarterSorting([SortKey.NAME, direction])}
          />
        </div>
        <div key="sort-offset">
          <SortSelect
            sortKey={SortKey.OFFSET}
            sortDirection={starterSorting?.[0] === SortKey.OFFSET ? starterSorting[1] : null}
            onSort={(direction) => setStarterSorting([SortKey.OFFSET, direction])}
          />
        </div>
        <div key="deselect-tag-button">
          <div
            className="starter-list-button"
            {...onClickGenerator(() => {
              setStarterSorting(null);
              setIsSortOpen(false);
            })}
          >
            {t('list.RESET')}
          </div>
        </div>
      </div>
    );
  }

  function renderPoiRow(poi: PoiType, style: ItemStyle, isFavorite = false) {
    return (
      <div key={`poi-${poi.id}-${isFavorite}`} style={style}>
        <PoiListItem poi={poi} onClick={scrollCorrection} />
      </div>
    );
  }

  function renderTrackRow(track: TrackType, style: ItemStyle, isFavorite = false) {
    return (
      <div key={`track-${track.id}-${isFavorite}`} style={style}>
        <TrackListItem track={track} onClick={scrollCorrection} />
      </div>
    );
  }

  const items: Array<SideBarItem> = useMemo(() => {
    if (searchText === '') {
      const items: Array<SideBarItem> = [];
      items.push({
        type: SideBarItemType.HEADER,
        label: SideBarSection.FAVORITE,
      });
      if (openSections.includes(SideBarSection.FAVORITE)) {
        for (const track of allCurrentTracks.filter((poi) => currentSelectedIds.includes(poi.id))) {
          items.push({ type: SideBarItemType.TRACK, track, isFavorite: true });
        }
        for (const poi of allCurrentPois.filter((poi) => currentSelectedIds.includes(poi.id))) {
          items.push({ type: SideBarItemType.POI, poi, isFavorite: true });
        }
        for (const starter of deduplicateStarters(
          allCurrentStarters.filter((starter) => currentSelectedIds.includes(starter.id)),
        )) {
          items.push({
            type: SideBarItemType.STARTER,
            starter,
            isFavorite: true,
          });
        }
      }
      if (showEventSelector) {
        items.push({
          type: SideBarItemType.HEADER,
          label: SideBarSection.EVENTS,
          eventType,
        });
        if (openSections.includes(SideBarSection.EVENTS)) {
          for (const event of allEvents) {
            items.push({ type: SideBarItemType.EVENT, event });
          }
        }
      }
      if (allCurrentTracks.length > 0) {
        items.push({
          type: SideBarItemType.HEADER,
          label: SideBarSection.TRACK,
        });
        if (openSections.includes(SideBarSection.TRACK)) {
          for (const track of allCurrentTracks) {
            items.push({
              type: SideBarItemType.TRACK,
              track: track,
            });
          }
        }
      }
      if (allCurrentPois.length > 0) {
        items.push({ type: SideBarItemType.HEADER, label: SideBarSection.POI });
        if (openSections.includes(SideBarSection.POI)) {
          for (const poi of allCurrentPois) {
            items.push({
              type: SideBarItemType.POI,
              poi: poi,
            });
          }
        }
      }
      if (allCurrentStarters.length > 0) {
        items.push({
          type: SideBarItemType.HEADER,
          label: SideBarSection.STARTERS,
        });
        if (openSections.includes(SideBarSection.STARTERS)) {
          if (isFilterOpen && availableTags.size > 0) {
            items.push({
              type: SideBarItemType.STARTER_FILTER,
              itemCount: availableTags.size,
            });
          }
          if (isSortOpen) {
            items.push({ type: SideBarItemType.STARTER_SORT });
          }
          if (availableStarterGroups.size > 0) {
            for (const [tagKey, tagValues] of availableStarterGroups) {
              for (const [tagValue] of tagValues) {
                items.push({
                  type: SideBarItemType.STARTER_GROUP,
                  tagKey,
                  tagValue,
                });
                if (openStarterGroups.includes(`${tagKey}-${tagValue}`)) {
                  const startersInGroup = sortStarters(
                    deduplicatedStarters.filter(
                      (starter) =>
                        starter.tags.get(tagKey) === tagValue ||
                        (tagValue === NULL_TAG && !starter.tags.has(tagKey)),
                    ),
                  );
                  for (const starter of startersInGroup) {
                    items.push({
                      type: SideBarItemType.STARTER,
                      starter,
                      group: `${tagKey}-${tagValue}`,
                    });
                  }
                }
              }
            }
            items.push({
              type: SideBarItemType.STARTER_GROUP,
              tagKey: ALL_TAG,
              tagValue: '',
            });
            if (openStarterGroups.includes(`${ALL_TAG}-`)) {
              for (const starter of sortStarters(deduplicatedStarters)) {
                items.push({
                  type: SideBarItemType.STARTER,
                  starter,
                  group: `${ALL_TAG}-`,
                });
              }
            }
          } else {
            for (const starter of sortStarters(deduplicatedStarters)) {
              items.push({
                type: SideBarItemType.STARTER,
                starter,
              });
            }
          }
        }
      }
      return items;
    }
    const wordsMatch = searchText.match(/\w+/gi);
    const words = wordsMatch != null ? wordsMatch.map((element) => element.toLowerCase()) : [];
    const pattern = `(${uniq(words).join('|')})`;
    const regexp = new RegExp(pattern, 'igm');

    const searchFilteredStarters = filteredStarters.filter(
      (starter) => starter.searchText.search(regexp) >= 0,
    );
    const filteredTracks = allCurrentTracks.filter((track) => track.name?.search(regexp) >= 0);
    const filteredPois = allCurrentPois.filter((poi) => poi.name?.search(regexp) >= 0);

    const items: Array<SideBarItem> = [];

    if (filteredTracks.length > 0) {
      items.push({ type: SideBarItemType.HEADER, label: SideBarSection.TRACK });
    }
    for (const track of filteredTracks) {
      items.push({
        type: SideBarItemType.TRACK,
        track: track,
      });
    }

    if (filteredPois.length > 0) {
      items.push({ type: SideBarItemType.HEADER, label: SideBarSection.POI });
    }
    for (const poi of filteredPois) {
      items.push({
        type: SideBarItemType.POI,
        poi: poi,
      });
    }

    if (searchFilteredStarters.length > 0) {
      items.push({
        type: SideBarItemType.HEADER,
        label: SideBarSection.STARTERS,
      });
    }
    if (isFilterOpen && availableTags.size > 0) {
      items.push({
        type: SideBarItemType.STARTER_FILTER,
        itemCount: availableTags.size,
      });
    }
    if (isSortOpen) {
      items.push({ type: SideBarItemType.STARTER_SORT });
    }
    if (openSections.includes(SideBarSection.STARTERS)) {
      for (const starter of deduplicateStarters(searchFilteredStarters)) {
        items.push({
          type: SideBarItemType.STARTER,
          starter,
        });
      }
    }

    return items;
  }, [
    allCurrentStarters,
    filteredStarters,
    deduplicatedStarters,
    currentEventIndices,
    allCurrentPois,
    allCurrentTracks,
    isFilterOpen,
    isSortOpen,
    availableTags,
    availableStarterGroups,
    currentSelectedIds,
    searchText,
    starterSorting,
    showEventSelector,
    eventType,
    allEvents,
    openSections,
    openStarterGroups,
  ]);

  const stickyIndices = findAllIndexes(items, (item) => item.type === SideBarItemType.HEADER);

  return (
    <div className="side-bar">
      <div className="side-bar-search">
        <form className="search-form" onSubmit={preventDefault}>
          <i
            className={classnames('fas', 'fa-chevron-left', 'close-icon')}
            {...onClickGenerator(() => {
              sendAnalyticsEvent('starter-list-close');
              onClose();
            })}
          />
          <i
            className={classnames('fas', 'fa-search', 'search-icon')}
            {...onClickGenerator((event) => {
              event.stopPropagation();
              sendAnalyticsEvent('starter-list-search');
              switchToSearchTab();
            })}
          />
          <i
            className={classnames('fas', 'fa-times', 'clear-icon')}
            style={{ display: searchText === '' ? 'none' : 'block' }}
            {...onClickGenerator(() => {
              sendAnalyticsEvent('starter-list-clear');
              setSearchText('');
            })}
          />
          <input
            placeholder={t('Search...')}
            value={searchText}
            onChange={(event) => {
              setSearchText(event.target.value);
            }}
            {...onClickGenerator(() => {
              switchToSearchTab();
            })}
          />
        </form>
        <div
          className={classnames('side-bar-toolbar-button', {
            active: currentTab === SideBarTab.SETTINGS,
          })}
          {...onClickGenerator(() => toggleSettingsTab())}
        >
          <i className="fa fa-cog" />
        </div>
        <ReloadButton />
      </div>
      <div ref={listContainerRef} style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        {currentTab === SideBarTab.SEARCH ? (
          <VirtualList
            ref={listRef}
            width="100%"
            height={listContainerBounds.height}
            style={{ flex: '1' }}
            className="starter-list"
            itemCount={items.length}
            itemSize={(index) => {
              const item = items[index];
              switch (item.type) {
                case SideBarItemType.HEADER:
                  return HEADER_HEIGHT;
                case SideBarItemType.STARTER_GROUP:
                  return HEADER_HEIGHT;
                case SideBarItemType.STARTER_FILTER:
                  return 40 + item.itemCount * 40 + 1;
                case SideBarItemType.STARTER_SORT:
                  return 40 + 3 * 40 + 1;
                default:
                  return ITEM_HEIGHT;
              }
            }}
            stickyIndices={stickyIndices}
            overscanCount={20}
            estimatedItemSize={ITEM_HEIGHT}
            onScroll={(scrollOffset) => {
              listScrollTopRef.current = scrollOffset;
            }}
            renderItem={({ index, style }) => {
              const item = items[index];
              switch (item.type) {
                case SideBarItemType.HEADER:
                  switch (item.label) {
                    case SideBarSection.FAVORITE:
                      return renderFavoriteHeader(style);
                    case SideBarSection.TRACK:
                      return renderTrackHeader(style);
                    case SideBarSection.POI:
                      return renderPoiHeader(style);
                    case SideBarSection.STARTERS:
                      return renderStarterHeader(style);
                    case SideBarSection.EVENTS:
                      return renderEventHeader(item.eventType, style);
                    default:
                      return null;
                  }
                case SideBarItemType.EVENT:
                  return renderEventRow(item.event, style);
                case SideBarItemType.STARTER:
                  return renderStarterRow(item.starter, style, item.isFavorite, item.group);
                case SideBarItemType.STARTER_FILTER:
                  return renderStarterFilterRow(style);
                case SideBarItemType.STARTER_SORT:
                  return renderStarterSortRow(style);
                case SideBarItemType.STARTER_GROUP:
                  return renderStarterGroupHeader(style, item.tagKey, item.tagValue);
                case SideBarItemType.POI:
                  return renderPoiRow(item.poi, style, item.isFavorite);
                case SideBarItemType.TRACK:
                  return renderTrackRow(item.track, style, item.isFavorite);
                default:
                  return null;
              }
            }}
          />
        ) : (
          <SettingsView />
        )}
      </div>
    </div>
  );
}

export function SideBarButton({
  isOpen,
  onToggle,
}: {
  isOpen: boolean;
  onToggle: () => void;
}): JSX.Element {
  return (
    <div className={classnames('search-button-container', { 'is-open': isOpen })}>
      <div
        className="search-button"
        {...onClickGenerator(() => {
          onToggle();
        })}
      >
        <i className="fas fa-search" />
      </div>
    </div>
  );
}
